/**
 * Etiquetas de un cliente: chips + editor.
 *
 * Son la pieza de la que cuelga la segmentación de campañas, así que viven en
 * la cabecera de la ficha y no escondidas en una pestaña: lo primero que hay
 * que saber de alguien es de qué grupo es.
 *
 * El editor guarda el conjunto ENTERO (`PUT /clients/:id/tags`) en vez de ir
 * añadiendo y quitando de una en una: así no quedan estados a medias si se
 * cierra el desplegable a mitad.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Plus, Tag as TagIcon, X } from 'lucide-react'
import { api, apiErrorMessage } from '../../lib/api'
import { toast } from '../../lib/dialogs'
import type { ClientTagDto, ClientTagListResponse } from '@toga/shared'

/** Chip de una etiqueta, en los colores que existen en los dos temas. */
const CHIP: Record<string, string> = {
  slate: 'bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/35',
  toga: 'bg-toga-500/15 text-toga-700 dark:text-toga-200 border-toga-500/35',
  sky: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/35',
  emerald: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/35',
  amber: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/35',
  rose: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/35',
  purple: 'bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/35',
  fuchsia: 'bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-200 border-fuchsia-500/35',
  teal: 'bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/35',
}

export function TagChip({ tag, onRemove }: { tag: ClientTagDto; onRemove?: () => void }) {
  const { t } = useTranslation('clients')
  return (
    <span
      title={tag.description ?? undefined}
      className={`inline-flex items-center gap-1 pl-2 ${onRemove ? 'pr-1' : 'pr-2'} py-0.5 rounded-full border text-xs ${
        CHIP[tag.color] ?? CHIP.slate
      }`}
    >
      {tag.name}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={t('tags.remove', { name: tag.name })}
          className="w-4 h-4 grid place-items-center rounded-full hover:bg-black/10 dark:hover:bg-white/15"
        >
          <X size={10} />
        </button>
      )}
    </span>
  )
}

interface Props {
  clientId: string
  tags: ClientTagDto[]
  canWrite: boolean
  onChange: (tags: ClientTagDto[]) => void
}

export default function ClientTags({ clientId, tags, canWrite, onChange }: Props) {
  const { t } = useTranslation('clients')
  const [open, setOpen] = useState(false)
  const [catalog, setCatalog] = useState<ClientTagDto[] | null>(null)
  const [query, setQuery] = useState('')
  const [saving, setSaving] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open || catalog) return
    void api
      .get<ClientTagListResponse>('/client-tags')
      .then((r) => setCatalog(r.items))
      .catch(() => setCatalog([]))
  }, [open, catalog])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const guardar = useCallback(
    async (siguientes: ClientTagDto[]) => {
      setSaving(true)
      try {
        const { tags: guardadas } = await api.put<{ tags: ClientTagDto[] }>(
          `/clients/${clientId}/tags`,
          { tagIds: siguientes.map((x) => x.id) },
        )
        onChange(guardadas)
      } catch (err) {
        toast.error(apiErrorMessage(err, t('tags.saveError')))
      } finally {
        setSaving(false)
      }
    },
    [clientId, onChange, t],
  )

  const puestas = new Set(tags.map((x) => x.id))
  const texto = query.trim()
  const visibles = (catalog ?? []).filter((x) =>
    texto ? x.name.toLowerCase().includes(texto.toLowerCase()) : true,
  )
  // Solo se ofrece crear si no existe ya una con ese nombre exacto.
  const puedeCrear =
    texto.length > 0 && !(catalog ?? []).some((x) => x.name.toLowerCase() === texto.toLowerCase())

  const crear = async () => {
    setSaving(true)
    try {
      const { tag } = await api.post<{ tag: ClientTagDto }>('/client-tags', { name: texto })
      setCatalog((prev) => [...(prev ?? []), tag].sort((a, b) => a.name.localeCompare(b.name)))
      setQuery('')
      await guardar([...tags, tag])
    } catch (err) {
      toast.error(apiErrorMessage(err, t('tags.createError')))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex items-center gap-1.5 flex-wrap" ref={boxRef}>
      <TagIcon size={13} className="t-faint shrink-0" />
      {tags.length === 0 && !canWrite && <span className="text-xs t-faint">{t('tags.none')}</span>}
      {tags.map((tag) => (
        <TagChip
          key={tag.id}
          tag={tag}
          onRemove={canWrite ? () => void guardar(tags.filter((x) => x.id !== tag.id)) : undefined}
        />
      ))}

      {canWrite && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            disabled={saving}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-dashed border-base text-xs t-muted hover:t-primary hover:border-solid disabled:opacity-50"
          >
            <Plus size={11} /> {tags.length === 0 ? t('tags.add') : ''}
          </button>

          {open && (
            <div
              className="absolute left-0 top-full mt-1 z-30 w-64 surface-deep p-2 shadow-xl"
              style={{ background: 'var(--bg-surface-strong)' }}
            >
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('tags.searchPlaceholder')}
                className="w-full px-2 py-1.5 mb-2 rounded bg-[var(--bg-input)] border border-[var(--border)] t-primary text-sm focus:outline-none focus:border-toga-500/50"
              />
              <div className="max-h-56 overflow-y-auto flex flex-col gap-0.5">
                {catalog === null && <span className="text-xs t-faint px-1 py-2">{t('tags.loading')}</span>}
                {catalog !== null && visibles.length === 0 && !puedeCrear && (
                  <span className="text-xs t-faint px-1 py-2">{t('tags.empty')}</span>
                )}
                {visibles.map((tag) => {
                  const puesta = puestas.has(tag.id)
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() =>
                        void guardar(puesta ? tags.filter((x) => x.id !== tag.id) : [...tags, tag])
                      }
                      className="w-full flex items-center justify-between gap-2 px-1.5 py-1 rounded text-left hover:bg-hover"
                    >
                      <TagChip tag={tag} />
                      {puesta && <Check size={13} className="text-emerald-500 shrink-0" />}
                    </button>
                  )
                })}
                {puedeCrear && (
                  <button
                    type="button"
                    onClick={() => void crear()}
                    className="w-full flex items-center gap-1.5 px-1.5 py-1.5 rounded text-left text-sm t-secondary hover:bg-hover"
                  >
                    <Plus size={12} /> {t('tags.create', { name: texto })}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
